/**
 * « Parcours de cours » (IELTS D-105, TCF D-107): the tree levels → units → lessons with their status
 * (draft, reviewed, published), and the sheet of a lesson: objectives, explanation in English and
 * French, vocabulary, exercises with a preview of what the learner sees, review and publication
 * (anti-plagiarism filter on the server).
 */

import { type Course, type Lesson, useAcademyCoursesList, useAcademyFormatRetrieve, useAcademyLessonsPublishCreate, useAcademyLessonsRetrieve, useAcademyLessonsReviewCreate } from "@agencyos/api-client";
import { contentLanguage, parseAppliesTo, parseDoAvoid } from "@agencyos/core";
import { Badge, EmptyState, GlassCard, GlassSheet, SectionHeader, Skeleton, StatCard } from "@agencyos/ui-web";
import { BookOpenCheck, ChevronDown, ChevronRight, FilePen, GraduationCap, Layers, ListChecks } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import "./ielts-admin.css";
import { localized } from "../../components/TranslatedFields";
import { useAdminExam } from "./examContext";
import { ExercisePreview } from "./ExercisePreview";
import { levelLabels, objectivesOf, sumCounts, textIn, type UnitNode, unitsOf, vocabularyOf } from "./ieltsData";
import { useLevelLessons } from "./ieltsHooks";
import { LessonText, ValidationPanel } from "./ieltsShared";
import { OriginalContentBanner } from "./QuestionsTab";
import { percent, StatusBadge, useAcademyRoles, useRefreshAcademy } from "./shared";

const COUNTED = ["published", "reviewed", "draft"] as const;

function LessonStats({ lesson }: { lesson: Lesson }) {
  const { t } = useTranslation();
  const byType = lesson.exercises.reduce<Record<string, number>>((acc, e) => ({ ...acc, [e.type]: (acc[e.type] ?? 0) + 1 }), {});
  return (
    <section className="stack" aria-labelledby={`lesson-stats-${lesson.id}`}>
      <h3 id={`lesson-stats-${lesson.id}`} className="ao-headline">
        {t("academyAdmin.ielts.course.stats")}
      </h3>
      <div className="ielts-stats">
        <div className="ielts-stat">
          <span className="ao-footnote ao-secondary">{t("academyAdmin.ielts.course.exercises")}</span>
          <strong className="ao-title-3 ao-num">{lesson.exercises.length}</strong>
        </div>
        <div className="ielts-stat">
          <span className="ao-footnote ao-secondary">{t("academyAdmin.similarityShort")}</span>
          <strong className="ao-title-3 ao-num">{percent(lesson.similarity)}</strong>
        </div>
        <div className="ielts-stat ielts-stat--wide">
          <span className="ao-footnote ao-secondary">{t("academyAdmin.ielts.course.types")}</span>
          <span className="ielts-chips">
            {Object.entries(byType).map(([type, n]) => (
              <Badge key={type}>{t("academyAdmin.ielts.course.typeCount", { type: t(`academyAdmin.ielts.exercise.type.${type}`, { defaultValue: type }), count: n })}</Badge>
            ))}
          </span>
        </div>
      </div>
      <p className="muted ao-footnote">{t("academyAdmin.ielts.course.statsNote")}</p>
    </section>
  );
}

/** « À faire / À éviter » and what a method lesson helps with (D-107). */
function MethodFacts({ lesson }: { lesson: Lesson }) {
  const { t, i18n } = useTranslation();
  const doAvoid = parseDoAvoid(lesson.do_avoid, i18n.language);
  const applies = parseAppliesTo(lesson.applies_to);
  if (!doAvoid.doList.length && !doAvoid.avoidList.length && !applies.length) return null;
  return (
    <section className="stack" aria-labelledby={`lesson-method-${lesson.id}`}>
      <h3 id={`lesson-method-${lesson.id}`} className="ao-headline">
        {t("academyCourse.admin.method")}
      </h3>
      {applies.length ? (
        <span className="ielts-chips">
          {applies.map((a) => (
            <Badge key={`${a.skill}-${a.task}-${a.type}`}>{[a.skill.toUpperCase(), a.task && t("academyCourse.admin.task", { n: a.task }), a.type].filter(Boolean).join(" · ")}</Badge>
          ))}
        </span>
      ) : null}
      <div className="ielts-two">
        <article className="ao-surface ao-card stack ielts-start">
          <Badge tone="success">{t("academyCourse.method.do")}</Badge>
          <ul className="ielts-objectives">
            {doAvoid.doList.map((d) => (
              <li key={d}>{d}</li>
            ))}
          </ul>
        </article>
        <article className="ao-surface ao-card stack ielts-start">
          <Badge tone="danger">{t("academyCourse.method.avoid")}</Badge>
          <ul className="ielts-objectives">
            {doAvoid.avoidList.map((d) => (
              <li key={d}>{d}</li>
            ))}
          </ul>
        </article>
      </div>
    </section>
  );
}

function LessonBody({ lesson, onChange }: { lesson: Lesson; onChange: (lesson: Lesson) => void }) {
  const { t } = useTranslation();
  const { exam } = useAdminExam();
  const ielts = exam === "ielts_general";
  const lang = contentLanguage(exam); // D-123: German content is German (it was marked French)
  const { canEdit } = useAcademyRoles();
  const refresh = useRefreshAcademy();
  const review = useAcademyLessonsReviewCreate({ mutation: { onSuccess: refresh } });
  const publish = useAcademyLessonsPublishCreate({ mutation: { onSuccess: refresh } });
  const objectives = objectivesOf(lesson.objectives, lang);
  const vocabulary = vocabularyOf(lesson.vocabulary);
  const en = textIn(lesson.explanation, "en");
  const fr = textIn(lesson.explanation, "fr");
  return (
    <div className="stack ielts-lesson">
      <div className="row">
        <StatusBadge status={lesson.status} />
        <span className="code-chip">{lesson.code}</span>
        <span className="muted">{t("academyAdmin.similarity", { value: percent(lesson.similarity) })}</span>
      </div>
      <OriginalContentBanner />
      <section className="stack" aria-labelledby={`lesson-objectives-${lesson.id}`}>
        <h3 id={`lesson-objectives-${lesson.id}`} className="ao-headline">
          {t("academyAdmin.ielts.course.objectives")}
        </h3>
        {objectives.length ? (
          <ul className="ielts-objectives" lang={lang}>
            {objectives.map((o) => (
              <li key={o}>{o}</li>
            ))}
          </ul>
        ) : (
          <p className="muted">{t("academyAdmin.ielts.course.none")}</p>
        )}
      </section>
      <section className="stack" aria-labelledby={`lesson-explanation-${lesson.id}`}>
        <h3 id={`lesson-explanation-${lesson.id}`} className="ao-headline">
          {t("academyAdmin.ielts.course.explanation")}
        </h3>
        <div className="ielts-two">
          <article className="ao-surface ao-card stack ielts-start">
            <Badge tone="info">{t("academyAdmin.ielts.course.inEnglish")}</Badge>
            {en ? <LessonText text={en} lang="en" /> : <p className="muted">{t("academyAdmin.ielts.course.none")}</p>}
          </article>
          <article className="ao-surface ao-card stack ielts-start">
            <Badge>{t("academyAdmin.ielts.course.inFrench")}</Badge>
            {fr ? <LessonText text={fr} lang="fr" /> : <p className="muted">{t("academyAdmin.ielts.course.none")}</p>}
          </article>
        </div>
      </section>
      <section className="stack" aria-labelledby={`lesson-vocabulary-${lesson.id}`}>
        <h3 id={`lesson-vocabulary-${lesson.id}`} className="ao-headline">
          {t("academyAdmin.ielts.course.vocabulary")}
        </h3>
        {vocabulary.length ? (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("academyAdmin.ielts.course.word")}</th>
                  <th>{t("academyAdmin.ielts.course.definition")}</th>
                  <th>{t("academyAdmin.ielts.course.example")}</th>
                  <th>{ielts ? t("academyAdmin.ielts.course.french") : t("academyCourse.admin.english")}</th>
                </tr>
              </thead>
              <tbody>
                {vocabulary.map((v) => (
                  <tr key={v.word}>
                    <td lang={lang}>
                      <strong>{v.word}</strong>
                    </td>
                    <td lang={lang}>{v.definition || "—"}</td>
                    <td lang={lang}>{v.example || "—"}</td>
                    <td lang={ielts ? "fr" : "en"}>{(ielts ? v.fr : v.en) || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="muted">{t("academyAdmin.ielts.course.none")}</p>
        )}
      </section>
      <section className="stack" aria-labelledby={`lesson-exercises-${lesson.id}`}>
        <h3 id={`lesson-exercises-${lesson.id}`} className="ao-headline">
          {t("academyAdmin.ielts.course.exercisesCount", { count: lesson.exercises.length })}
        </h3>
        <p className="muted">{t("academyAdmin.ielts.course.previewHint")}</p>
        {lesson.exercises.length ? (
          <ol className="ielts-exercises">
            {lesson.exercises.map((exercise, i) => (
              <li key={exercise.id}>
                <ExercisePreview exercise={exercise} index={i + 1} />
              </li>
            ))}
          </ol>
        ) : (
          <p className="muted">{t("academyAdmin.ielts.course.noExercise")}</p>
        )}
      </section>
      <MethodFacts lesson={lesson} />
      <LessonStats lesson={lesson} />
      {canEdit ? (
        <ValidationPanel
          id={`lesson-validation-${lesson.id}`}
          status={lesson.status}
          pending={review.isPending || publish.isPending}
          reviewError={review.error}
          publishError={publish.error}
          done={review.isSuccess || publish.isSuccess}
          onReview={() => {
            publish.reset();
            review.mutate({ id: lesson.id }, { onSuccess: (r) => r.status === 200 && onChange(r.data) });
          }}
          onPublish={() => {
            review.reset();
            publish.mutate({ id: lesson.id }, { onSuccess: (r) => r.status === 200 && onChange(r.data) });
          }}
        />
      ) : null}
    </div>
  );
}

function LessonSheet({ lesson, onClose }: { lesson: Lesson; onClose: () => void }) {
  const { i18n } = useTranslation();
  const detail = useAcademyLessonsRetrieve(lesson.id);
  const [current, setCurrent] = useState<Lesson | null>(null);
  const loaded = current ?? (detail.data?.status === 200 ? detail.data.data : lesson);
  return (
    <GlassSheet open title={localized(loaded.title, i18n.language) || loaded.code} onClose={onClose}>
      <LessonBody lesson={loaded} onChange={setCurrent} />
    </GlassSheet>
  );
}

function UnitSection({ unit, lessons, onOpen }: { unit: UnitNode; lessons: Lesson[] | undefined; onOpen: (lesson: Lesson) => void }) {
  const { t, i18n } = useTranslation();
  const { exam } = useAdminExam();
  const lang = contentLanguage(exam); // D-123: German content is German (it was marked French)
  const [open, setOpen] = useState(true);
  const panelId = `unit-${unit.id}`;
  return (
    <li className="ielts-unit ao-surface ao-card">
      <button type="button" className="ielts-unit__head" aria-expanded={open} aria-controls={panelId} onClick={() => setOpen((o) => !o)}>
        {open ? <ChevronDown size={16} strokeWidth={1.75} aria-hidden="true" /> : <ChevronRight size={16} strokeWidth={1.75} aria-hidden="true" />}
        <span className="code-chip">{unit.code}</span>
        <span className="ielts-unit__title">
          <strong lang={lang}>{localized(unit.title, lang) || unit.code}</strong>
          {localized(unit.title, i18n.language) !== localized(unit.title, lang) ? <span className="ao-footnote ao-secondary">{localized(unit.title, i18n.language)}</span> : null}
        </span>
        <span className="ielts-chips ielts-unit__counts">
          {COUNTED.map((s) =>
            unit.counts[s] ? (
              <Badge key={s} tone={s === "published" ? "success" : s === "reviewed" ? "offer" : "default"}>
                {t(`academyAdmin.ielts.course.countBy.${s}`, { count: unit.counts[s] })}
              </Badge>
            ) : null,
          )}
        </span>
      </button>
      {open ? (
        <div id={panelId}>
          {lessons === undefined ? (
            <Skeleton style={{ height: 96 }} />
          ) : lessons.length ? (
            <ul className="ao-list" aria-label={t("academyAdmin.ielts.course.lessonsOf", { unit: unit.code })}>
              {lessons.map((lesson) => (
                <li key={lesson.id}>
                  <button type="button" className="ao-list__row ielts-lesson-row" onClick={() => onOpen(lesson)}>
                    <span className="ao-num ao-secondary ielts-lesson-row__code">{lesson.code}</span>
                    <span className="ielts-lesson-row__title" lang={lang}>
                      {localized(lesson.title, lang) || lesson.code}
                    </span>
                    <span className="ao-footnote ao-secondary ielts-nowrap">{t("academyAdmin.ielts.course.exercisesShort", { count: lesson.exercises.length })}</span>
                    <StatusBadge status={lesson.status} />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted ielts-unit__empty">{t("academyAdmin.ielts.course.noLesson")}</p>
          )}
        </div>
      ) : null}
    </li>
  );
}

export function CourseTab() {
  const { t, i18n } = useTranslation();
  const { exam, ielts } = useAdminExam();
  // the exam is always sent: without it the trainers' list mixes both exams
  const courses = useAcademyCoursesList({ exam, page: 1 });
  const params = exam === "tcf_canada" ? {} : { exam }; // D-123: the TEF and German formats (they read the TCF's)
  const format = useAcademyFormatRetrieve(params);
  const labels = useMemo(() => levelLabels(format.data?.status === 200 ? format.data.data.details : undefined), [format.data]);
  const rows: Course[] = courses.data?.status === 200 ? [...courses.data.data.results].sort((a, b) => a.order - b.order) : [];
  const [picked, setPicked] = useState("");
  const course = rows.find((c) => c.level === picked) ?? rows[0];
  const lessons = useLevelLessons(exam, course?.level ?? "");
  const [open, setOpen] = useState<Lesson | null>(null);
  const close = useCallback(() => setOpen(null), []);
  const allUnits = rows.flatMap((c) => unitsOf(c.units));
  const totals = sumCounts(allUnits);
  const units = course ? unitsOf(course.units) : [];
  const byUnit = (code: string) => (lessons.data ? lessons.data.filter((l) => l.unit_code === code) : undefined);
  const levelName = (c: Course) => localized(labels[c.level], i18n.language) || localized(c.title, i18n.language) || c.level;

  return (
    <GlassCard as="section" className="stack ielts-course" aria-labelledby="ielts-course-title">
      <SectionHeader
        id="ielts-course-title"
        title={ielts ? t("academyAdmin.ielts.course.title") : t("academyAdmin.course.title", { exam: t(`ielts.exams.${exam}.name`) })}
        subtitle={ielts ? t("academyAdmin.ielts.course.intro") : t("academyAdmin.course.intro")}
      />
      {courses.data === undefined ? (
        <Skeleton style={{ height: 240 }} />
      ) : !rows.length ? (
        <EmptyState icon={<GraduationCap size={22} strokeWidth={1.75} />} title={t("academyAdmin.ielts.course.empty")} description={t("academyAdmin.ielts.course.emptyHint")} />
      ) : (
        <>
          <section className="kpis" aria-label={t("academyAdmin.ielts.course.figures")}>
            <StatCard icon={<Layers size={18} strokeWidth={1.75} />} label={t("academyAdmin.ielts.course.levels")} value={rows.length} hint={t("academyAdmin.ielts.course.unitsCount", { count: allUnits.length })} tone="accent" />
            <StatCard icon={<BookOpenCheck size={18} strokeWidth={1.75} />} label={t("academyAdmin.status.published")} value={totals.published} hint={t("academyAdmin.ielts.course.ofLessons", { count: totals.published + totals.reviewed + totals.draft })} tone="success" />
            <StatCard icon={<ListChecks size={18} strokeWidth={1.75} />} label={t("academyAdmin.status.reviewed")} value={totals.reviewed} hint={t("academyAdmin.ielts.course.toPublish")} tone="warning" />
            <StatCard icon={<FilePen size={18} strokeWidth={1.75} />} label={t("academyAdmin.status.draft")} value={totals.draft} hint={t("academyAdmin.ielts.course.toReview")} tone="neutral" />
          </section>
          <div className="ielts-course__layout">
            <nav className="ielts-levels" aria-label={t("academyAdmin.ielts.course.levels")}>
              <ul className="ao-list">
                {rows.map((c) => {
                  const counts = sumCounts(unitsOf(c.units));
                  const total = counts.published + counts.reviewed + counts.draft;
                  return (
                    <li key={c.id}>
                      <button type="button" className={`ao-list__row ielts-level${course?.id === c.id ? " ielts-level--active" : ""}`} aria-pressed={course?.id === c.id} onClick={() => setPicked(c.level)}>
                        <span className="ielts-level__text">
                          <strong>{levelName(c)}</strong>
                          <span className="ao-footnote ao-secondary">{ielts ? t("academyAdmin.ielts.course.levelMeta", { cefr: c.cefr || "—", band: c.band_target || "—" }) : exam === "goethe" ? t("academyCourse.admin.levelCefr", { cefr: c.cefr || "—" }) : t("academyCourse.admin.levelMeta", { cefr: c.cefr || "—", nclc: c.band_target || "—" })}</span>
                        </span>
                        <span className="ao-num ao-footnote ao-secondary">{t("academyAdmin.ielts.course.publishedOf", { published: counts.published, total })}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>
            {course ? (
              <section className="stack ielts-units" aria-labelledby="ielts-level-title">
                <div className="row between">
                  <h3 id="ielts-level-title" className="ao-title-3">
                    {levelName(course)}
                  </h3>
                  <span className="muted">{t("academyAdmin.ielts.course.unitsCount", { count: units.length })}</span>
                </div>
                {lessons.isError ? <p className="ko">{t("common.error")}</p> : null}
                {units.length ? (
                  <ul className="ielts-unit-list">
                    {units.map((unit) => (
                      <UnitSection key={unit.id} unit={unit} lessons={byUnit(unit.code)} onOpen={setOpen} />
                    ))}
                  </ul>
                ) : (
                  <p className="muted">{t("academyAdmin.ielts.course.noUnit")}</p>
                )}
              </section>
            ) : null}
          </div>
        </>
      )}
      {open ? <LessonSheet key={open.id} lesson={open} onClose={close} /> : null}
    </GlassCard>
  );
}
